import { sessionToken } from '../auth'
import type { Storage } from '../../platform/storage'
import { syncNow } from './exchange'

/**
 * Отправка при каждом изменении.
 *
 * Обмен только при входе — это разовая загрузка, а не синхронизация: человек меняет
 * профиль или пишет заметку, а на сервер это попадает неизвестно когда. Поэтому каждое
 * изменение просит обмен, а близкие по времени изменения сливаются в одну отправку.
 */

const DEBOUNCE_MS = 1200

let bound: Storage | null = null
let onApplied: (() => void) | null = null
let timer: ReturnType<typeof setTimeout> | null = null
let running = false
/** Изменения, которые ещё не уехали: либо ждут таймера, либо прошлая попытка не удалась. */
let dirty = false

/** Подключает обмен к хранилищу и говорит, что делать, когда сервер прислал новое. */
export function bindSync(storage: Storage, applied: () => void): () => void {
  bound = storage
  onApplied = applied

  const retry = () => {
    if (dirty) scheduleSync(0)
  }
  // Вернулись в сеть или на вкладку — дошлём то, что не ушло.
  globalThis.addEventListener('online', retry)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') retry()
    else void flushSync()
  })
  // Закрывают вкладку — отправляем немедленно, не дожидаясь таймера.
  globalThis.addEventListener('pagehide', () => void flushSync())

  return () => {
    globalThis.removeEventListener('online', retry)
  }
}

/** Просит обмен. Близкие изменения сливаются в одну отправку. */
export function scheduleSync(delayMs = DEBOUNCE_MS): void {
  if (bound === null || sessionToken() === null) return
  dirty = true
  if (timer !== null) clearTimeout(timer)
  timer = setTimeout(() => void run(), delayMs)
}

/**
 * Отправляет немедленно: при уходе со страницы ждать таймера нельзя.
 * Возвращает false, если что-то осталось неотправленным, — по этому признаку
 * выход из аккаунта не стирает данные, которые ещё не уехали (разбор 12.10.2026).
 */
export async function flushSync(): Promise<boolean> {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
  if (dirty) await run()
  return !dirty
}

async function run(): Promise<void> {
  if (bound === null) return
  if (running) {
    // Пока идёт обмен, новые изменения не теряем: попросим ещё один сразу после.
    dirty = true
    return
  }
  running = true
  dirty = false
  const ok = await syncNow(bound, onApplied)
  running = false
  if (!ok) dirty = true
  else if (dirty) scheduleSync(0)
}
